import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AreYouSureDialogComponent } from 'src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component';
import { Subject, of, merge, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, map, takeUntil, take, catchError, first } from 'rxjs/operators';
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';
import { AssististService } from '../assistist.service';
import { DocumentDTO } from '../Document';
import { DocumentCategoryDTO } from '../DocumentCategory';
import { DocumentCategoryKeywordDTO } from '../DocumentCategoryKeyword';
import { MediaService, Media } from 'src/app/content/media.service';
import { SessionService } from 'src/app/session.service';
import { TitleService } from 'src/app/title.service';
import { UiService } from 'src/app/ui/ui.service';
import { NotificationService } from 'src/app/notifications/notification.service';
import { CanComponentDeactivate } from 'src/app/shared/guards/can-deactivate.guard';
import { IntegrationsService } from 'src/app/integrations/integrations.service';
import { getAssististDocumentLabels } from '../document-labels';

@Component({
  selector: 'tripist-documents-edit',
  templateUrl: './documents-edit.component.html',
  styleUrls: ['./documents-edit.component.scss']
})
export class assististDocumentsEditComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private readonly mockToolNamesByProviderType: Record<string, { get_options: string; get_availability: string }> = {
    hotel_booking_engine: {
      get_options: 'hotel_get_options',
      get_availability: 'hotel_get_availability',
    },
    restaurant_booking_engine: {
      get_options: 'restaurant_get_options',
      get_availability: 'restaurant_get_availability',
    },
    services_booking_engine: {
      get_options: 'services_get_options',
      get_availability: 'services_get_availability',
    },
    equipment_booking_engine: {
      get_options: 'equipment_get_options',
      get_availability: 'equipment_get_availability',
    },
    events_booking_engine: {
      get_options: 'events_get_options',
      get_availability: 'events_get_availability',
    },
  };

  private readonly bookingEngineProviderTypes = new Set([
    'hotel_booking_engine',
    'restaurant_booking_engine',
    'services_booking_engine',
    'equipment_booking_engine',
    'events_booking_engine',
  ]);

  newDocument = false;
  document?: DocumentDTO;
  integrations: any[] = [];
  bookingEngineIntegrations: any[] = [];
  integrationID!: number;
  allCategories: DocumentCategoryDTO[] = [];
  categoryKeywords: DocumentCategoryKeywordDTO[] = [];
  featureType: string = ''; // 'tripist' or 'explorist'

  // for adding new categories
  categorySelectControl = new FormControl('');
  filteredCategories: DocumentCategoryDTO[] = [];

  // primary category selection
  primaryCategoryControl = new FormControl('');

  // per‐category "add tag" controls
  newTagControls: { [categoryId: number]: FormControl } = {};

  // autocomplete suggestions for tags per category
  filteredTags: { [categoryId: number]: string[] } = {};

  // media selection (if you want image preview)
  documentForm!: FormGroup;
  filteredMedias: Media[] = [];
  selectedMedia?: Media;
  filteredHeroMedias: Media[] = [];
  selectedHeroMedia?: Media;

  // File upload properties
  uploadFile: File | undefined;
  uploadImageWidth: number | undefined;
  uploadImageHeight: number | undefined;
  isUploading: boolean = false;
  heroUploadFile: File | undefined;
  heroUploadImageWidth: number | undefined;
  heroUploadImageHeight: number | undefined;
  isHeroUploading: boolean = false;
  
  // Flag to track if categories/keywords have been modified
  private categoriesModified = false;
  private mockDataModified = false;
  private isHydratingDocument = false;
  private previewBookingEngineIntegrationId: number | null = null;
  generatedMockData: any | null = null;
  generatedMockDataPromptSnapshot = '';
  isGeneratingMockData = false;
  mockBookingsCount = 0;
  
  /**
   * Set to true when navigation is initiated immediately after a successful
   * save.  This bypasses the unsaved-changes guard & beforeunload handler so
   * the user isn't prompted even though the form is still marked dirty.
   */
  private ignoreUnsavedChanges = false;

  private onDestroy$ = new Subject<void>();

  // Getter methods for display text
  get itemText(): string {
    return getAssististDocumentLabels(this.featureType).singular;
  }

  get createItemText(): string {
    return `Create ${getAssististDocumentLabels(this.featureType).singular}`;
  }

  get editItemText(): string {
    return `Edit ${getAssististDocumentLabels(this.featureType).singular}`;
  }

  get saveNewItemText(): string {
    return `Save New ${getAssististDocumentLabels(this.featureType).singular}`;
  }

  get deleteItemText(): string {
    return `Delete ${getAssististDocumentLabels(this.featureType).singular}`;
  }

  get saveItemUpdatesText(): string {
    return `Save ${getAssististDocumentLabels(this.featureType).singular} Updates`;
  }

  get itemDetailsText(): string {
    return `${getAssististDocumentLabels(this.featureType).singular} Details`;
  }

  get subtitleLabel(): string {
    return getAssististDocumentLabels(this.featureType).subtitleLabel;
  }

  get isBookingEngineSelected(): boolean {
    return !!this.documentForm?.get('booking_engine_integration_id')?.value;
  }

  get selectedBookingEngineIntegration(): any | null {
    const selectedId = this.documentForm?.get('booking_engine_integration_id')?.value;
    if (!selectedId) {
      return null;
    }
    return this.bookingEngineIntegrations.find((integration: any) => integration.id === selectedId) || null;
  }

  get isMockBookingEngineSelected(): boolean {
    return !!this.selectedBookingEngineIntegration?.provider?.is_mock_booking_engine;
  }

  get selectedBookingEngineProviderType(): string | null {
    return this.selectedBookingEngineIntegration?.provider?.type || null;
  }

  get hasGeneratedMockData(): boolean {
    return !!this.parseMockData(this.generatedMockData);
  }

  get canClearMockBookings(): boolean {
    return !this.newDocument && this.isMockBookingEngineSelected && this.mockBookingsCount > 0;
  }

  get hasStaleMockDataPreview(): boolean {
    const currentPrompt = String(this.documentForm?.get('mock_data_prompt')?.value || '').trim();
    const snapshotPrompt = String(this.generatedMockDataPromptSnapshot || '').trim();
    return this.hasGeneratedMockData && currentPrompt !== snapshotPrompt;
  }

  get mockOptionsPayload(): any | null {
    return this.getMockPayloadByAction('get_options');
  }

  get mockAvailabilityPayload(): any | null {
    return this.getMockPayloadByAction('get_availability');
  }

  get hasRenderableMockPayloads(): boolean {
    return !!this.mockOptionsPayload || !!this.mockAvailabilityPayload;
  }

  get activeMockRendererType(): 'hotel' | 'restaurant' | 'services' | 'equipment' | 'events' | 'generic' {
    const providerType = this.selectedBookingEngineProviderType;
    if (providerType === 'hotel_booking_engine') {
      return 'hotel';
    }
    if (providerType === 'restaurant_booking_engine') {
      return 'restaurant';
    }
    if (providerType === 'services_booking_engine') {
      return 'services';
    }
    if (providerType === 'equipment_booking_engine') {
      return 'equipment';
    }
    if (providerType === 'events_booking_engine') {
      return 'events';
    }
    return 'generic';
  }

  get mockOptionsList(): any[] {
    const payload = this.mockOptionsPayload;
    return Array.isArray(payload?.options) ? payload.options : [];
  }

  get mockAvailabilitySlots(): any[] {
    const payload = this.mockAvailabilityPayload;
    return Array.isArray(payload?.availability?.slots) ? payload.availability.slots : [];
  }

  get mockOperatingDays(): string[] {
    const days = this.mockAvailabilityPayload?.availability?.operating_days;
    return Array.isArray(days) ? days : [];
  }

  get mockOperatingHoursEntries(): { day: string; windows: string[] }[] {
    const source = this.mockAvailabilityPayload?.availability?.operating_hours_local;
    if (!source || typeof source !== 'object') {
      return [];
    }
    return Object.keys(source).map((dayKey: string) => ({
      day: dayKey,
      windows: Array.isArray(source[dayKey]) ? source[dayKey] : [],
    }));
  }

  get mockInventoryByOption(): { option_id: string; total_units: number }[] {
    const rows = this.mockAvailabilityPayload?.availability?.inventory_by_option;
    return Array.isArray(rows) ? rows : [];
  }

  get mockCapacityByOption(): { option_id: string; total_units: number }[] {
    const rows = this.mockAvailabilityPayload?.availability?.capacity_by_option;
    return Array.isArray(rows) ? rows : [];
  }

  get mockRestaurantRequestedMealPeriod(): string | null {
    const value = this.mockOptionsPayload?.meal_period || this.mockAvailabilityPayload?.availability?.meal_period;
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
  }

  get mockRestaurantOfferedMealPeriods(): string[] {
    const fromOptions = this.mockOptionsPayload?.available_meal_periods;
    if (Array.isArray(fromOptions)) {
      return fromOptions.filter((value: any) => typeof value === 'string' && value.trim().length > 0);
    }
    const fromAvailability = this.mockAvailabilityPayload?.availability?.offered_meal_periods;
    if (Array.isArray(fromAvailability)) {
      return fromAvailability.filter((value: any) => typeof value === 'string' && value.trim().length > 0);
    }
    return [];
  }

  get mockRestaurantMealPeriodEntries(): { day: string; breakfast: string[]; lunch: string[]; dinner: string[] }[] {
    const source = this.mockAvailabilityPayload?.availability?.meal_periods_local;
    if (!source || typeof source !== 'object') {
      return [];
    }
    const dayKeys = Object.keys(source);
    if (dayKeys.length === 0) {
      return [];
    }

    const normalizeWindows = (value: any): string[] => Array.isArray(value)
      ? value.filter((entry: any) => typeof entry === 'string' && entry.trim().length > 0)
      : [];

    const defaultShapePresent = ['breakfast', 'lunch', 'dinner'].some((key) => Array.isArray((source as any)[key]));
    if (defaultShapePresent) {
      return [{
        day: 'default',
        breakfast: normalizeWindows((source as any).breakfast),
        lunch: normalizeWindows((source as any).lunch),
        dinner: normalizeWindows((source as any).dinner),
      }];
    }

    return dayKeys.map((dayKey: string) => {
      const dayValue = (source as any)[dayKey];
      return {
        day: dayKey,
        breakfast: normalizeWindows(dayValue?.breakfast),
        lunch: normalizeWindows(dayValue?.lunch),
        dinner: normalizeWindows(dayValue?.dinner),
      };
    });
  }

  get mockEmployeeSchedules(): { employee_id: string; days: string[]; windows: string[] }[] {
    const rows = this.mockAvailabilityPayload?.availability?.employee_schedules;
    if (!Array.isArray(rows)) {
      return [];
    }
    return rows.map((row: any) => ({
      employee_id: row?.employee_id || '-',
      days: Array.isArray(row?.days) ? row.days : [],
      windows: Array.isArray(row?.windows) ? row.windows : [],
    }));
  }

  get mockEventInventoryMode(): string {
    const mode = this.mockAvailabilityPayload?.availability?.inventory_mode;
    return typeof mode === 'string' && mode.trim().length > 0 ? mode : 'general_admission';
  }

  get mockEventMaxTickets(): number | null {
    const maxTickets = this.mockAvailabilityPayload?.availability?.max_tickets;
    return typeof maxTickets === 'number' ? maxTickets : null;
  }

  get mockEventSoldTickets(): number | null {
    const soldTickets = this.mockAvailabilityPayload?.availability?.sold_tickets;
    return typeof soldTickets === 'number' ? soldTickets : null;
  }

  get mockEventTicketsRemaining(): number | null {
    const explicitRemaining = this.mockAvailabilityPayload?.availability?.tickets_remaining;
    if (typeof explicitRemaining === 'number') {
      return explicitRemaining;
    }
    if (this.mockEventMaxTickets != null && this.mockEventSoldTickets != null) {
      return Math.max(0, this.mockEventMaxTickets - this.mockEventSoldTickets);
    }
    return null;
  }

  get generateMockDataDisabled(): boolean {
    if (this.isGeneratingMockData) {
      return true;
    }
    if (!this.isMockBookingEngineSelected) {
      return true;
    }
    if (!this.documentForm?.get('booking_engine_integration_id')?.value) {
      return true;
    }
    if (this.documentForm.get('foreign_key')?.invalid) {
      return true;
    }
    const promptValue = String(this.documentForm.get('mock_data_prompt')?.value || '').trim();
    if (!promptValue) {
      return true;
    }
    return false;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private TitleService: TitleService,
    private session: SessionService,
    private uiService: UiService,
    private NotificationService: NotificationService,
    private assististService: AssististService,
    private integrationsService: IntegrationsService,
    private mediaService: MediaService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    // Subscribe to route parameter and data changes to handle navigation between tripist/explorist
    merge(
      this.activatedRoute.params,
      this.activatedRoute.data
    ).pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(() => {
      this.updateFromRouteParams();
    });
    
    // Also handle initial load
    this.updateFromRouteParams();
    
    // Add beforeunload listener to prevent accidental page close/refresh with unsaved changes
    this.addBeforeUnloadListener();
  }

  private updateFromRouteParams(): void {
    // Get the feature type from the route to determine provider type
    const featureType = this.activatedRoute.snapshot.params['featureType']; // 'tripist' | 'explorist' | 'aura'
    const providerType = featureType === 'tripist'
      ? 'tripist_assistant'
      : featureType === 'aura'
        ? 'aura_assistant'
        : 'explorist_assistant';

    this.featureType = featureType;

    // grab the resolved integrations & find the correct assistant one
    this.integrations = this.activatedRoute.snapshot.data.integrations || [];
    const integration = (this.integrations as any[]).find(i => i.provider.type === providerType);
    this.integrationID = integration.id;

    // grab the list of all categories from the route resolve
    const categoriesData: any = this.activatedRoute.snapshot.data.categories || {};
    this.allCategories = categoriesData.records || [];

    // determine if we're creating or editing
    const idParam = this.activatedRoute.snapshot.paramMap.get('id');
    this.newDocument = !idParam;

    // init the form & media‐search
    this.initForm();
    this.hookMediaSearch();
    this.selectedMedia = undefined;
    this.selectedHeroMedia = undefined;

    // prepare the category selector autocomplete
    this.setupCategorySelect();
    this.loadBookingEngineIntegrations();

    if (!this.newDocument) {
      // fetch existing document via integrationID, not accountID
      this.uiService.waitForEvent(
        this.assististService.getDocument(this.currentAccountID, this.integrationID, +idParam!)
      ).subscribe((resp: any) => {
        this.isHydratingDocument = true;
        const doc = resp as DocumentDTO;
        this.document = doc;
        // patch basic fields
        this.TitleService.setTitle(
          this.TitleService.getTitle() + ` | ${doc.name}`
        );
        this.documentForm.patchValue({
          name: doc.name,
          subtitle: doc.subtitle,
          preview: doc.preview,
          content: doc.content,
          url: doc.url,
          foreign_key: doc.foreign_key,
          publish: doc.publish,
          publish_start_date: doc.publish_start_date ? this.formatDateForInput(doc.publish_start_date) : '',
          publish_end_date: doc.publish_end_date ? this.formatDateForInput(doc.publish_end_date) : '',
          always_include: doc.always_include ?? false,
          feature_start_date: (doc as any).feature_start_date ? this.formatDateForInput((doc as any).feature_start_date) : '',
          feature_end_date: (doc as any).feature_end_date ? this.formatDateForInput((doc as any).feature_end_date) : '',
          booking_engine_integration_id: (doc as any).booking_engine_integration_id || null,
          mock_data_prompt: '',
        }, { emitEvent: false });
        this.previewBookingEngineIntegrationId = this.documentForm.get('booking_engine_integration_id')?.value || null;
        this.generatedMockData = this.parseMockData((doc as any).mock_data);
        this.mockBookingsCount = Number((doc as any).mock_bookings_count || 0);
        this.generatedMockDataPromptSnapshot = '';
        // media
        if (doc.media) {
          this.selectedMedia = doc.media;
        }
        if ((doc as any).heroMedia) {
          this.selectedHeroMedia = (doc as any).heroMedia;
        }
        // category‐keyword relationships
        this.categoryKeywords = doc.documentCategoryKeywords || [];
        this.categoryKeywords.forEach(ck => {
          // Ensure is_primary is always a boolean (for backward compatibility)
          if (ck.is_primary === undefined || ck.is_primary === null) {
            ck.is_primary = false;
          }
          this.newTagControls[ck.category.id] = new FormControl('');
          this.setupTagAutocomplete(ck.category.id);
        });
        // Set primary category control to the current primary category
        const primaryCK = this.categoryKeywords.find(ck => ck.is_primary);
        if (primaryCK) {
          this.primaryCategoryControl.setValue(primaryCK.category.id);
        }
        this.updateFilteredCategories();
        
        // Mark form as pristine after loading existing data
        this.documentForm.markAsPristine();
        // Reset categories modified flag when loading existing data
        this.categoriesModified = false;
        this.mockDataModified = false;
        this.isHydratingDocument = false;
      });
    } else {
      this.TitleService.setTitle(this.createItemText);
      // no existing keywords
      this.categoryKeywords = [];
      this.updateFilteredCategories();
      this.generatedMockData = null;
      this.mockBookingsCount = 0;
      this.generatedMockDataPromptSnapshot = '';
      this.previewBookingEngineIntegrationId = null;
      this.mockDataModified = false;
    }
  }

  ngOnDestroy(): void {
    this.removeBeforeUnloadListener();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private initForm() {
    this.documentForm = new FormGroup({
      name: new FormControl('', Validators.required),
      subtitle: new FormControl('', Validators.required),
      preview: new FormControl('', Validators.required),
      content: new FormControl('', Validators.required),
      mediaSearch: new FormControl(''),
      heroMediaSearch: new FormControl(''),
      url: new FormControl(''),
      foreign_key: new FormControl(''),
      booking_engine_integration_id: new FormControl(null),
      mock_data_prompt: new FormControl(''),
      publish: new FormControl(false),
      publish_start_date: new FormControl(''),
      publish_end_date: new FormControl(''),
      always_include: new FormControl(false),
      feature_start_date: new FormControl(''),
      feature_end_date: new FormControl(''),
      imageName: new FormControl(''),  // Remove required validator - will be added conditionally
      heroImageName: new FormControl(''),
    }, { validators: [this.featureDateValidator.bind(this)] });

    this.updateForeignKeyValidation();
    this.documentForm.get('booking_engine_integration_id')?.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        this.updateForeignKeyValidation();
        if (this.isHydratingDocument) {
          this.previewBookingEngineIntegrationId = value || null;
          return;
        }
        const selectedIntegrationId = value || null;
        if (selectedIntegrationId !== this.previewBookingEngineIntegrationId) {
          this.previewBookingEngineIntegrationId = selectedIntegrationId;
          this.generatedMockData = null;
          this.generatedMockDataPromptSnapshot = '';
          this.mockDataModified = true;
        }
      });
  }

  private requiredTrimmedValueValidator(control: AbstractControl) {
    const value = control.value;
    if (value === null || value === undefined) {
      return { required: true };
    }
    if (typeof value === 'string' && value.trim() === '') {
      return { required: true };
    }
    return null;
  }

  private updateForeignKeyValidation(): void {
    const foreignKeyControl = this.documentForm.get('foreign_key');
    if (!foreignKeyControl) {
      return;
    }

    if (this.isBookingEngineSelected) {
      foreignKeyControl.setValidators([this.requiredTrimmedValueValidator.bind(this)]);
    } else {
      foreignKeyControl.clearValidators();
    }

    foreignKeyControl.updateValueAndValidity({ emitEvent: false });
  }

  private hookMediaSearch() {
    const bindMediaSearch = (controlName: string, context: string, apply: (medias: Media[]) => void) => {
      this.documentForm.get(controlName)!.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) =>
          this.mediaService.getMedias(this.currentAccountID, {
            page: 0,
            perPage: 10,
            context,
            keyword: term,
            forCount: false,
          } as any).pipe(catchError(() => of({ records: [] })))
        ),
        map((resp: any) => resp.records),
        takeUntil(this.onDestroy$)
      ).subscribe(medias => apply(medias));
    };

    bindMediaSearch('mediaSearch', `${this.featureType}_document_image`, (medias) => {
      this.filteredMedias = medias;
    });

    bindMediaSearch('heroMediaSearch', `${this.featureType}_document_hero_image`, (medias) => {
      this.filteredHeroMedias = medias;
    });
  }

  private loadBookingEngineIntegrations(): void {
    const appId = this.session.currentAppValue?.id;
    if (!appId) {
      this.bookingEngineIntegrations = [];
      return;
    }

    this.integrationsService.getIntegrations(this.currentAccountID, appId, false)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((results: any) => {
        const items = Array.isArray(results) ? results : (results?.data || []);
        this.bookingEngineIntegrations = (items || []).filter(
          (integration: any) => this.bookingEngineProviderTypes.has(String(integration?.provider?.type || ''))
        );
      });
  }

  // Implement media select & clear, mirroring categories-edit
  onMediaSelected(media: Media): void {
    this.selectedMedia = media;
    this.documentForm.get('mediaSearch')!.setValue('');
    // Mark form as dirty when image is selected
    this.documentForm.markAsDirty();
    // Also mark it as touched to ensure change detection
    this.documentForm.markAsTouched();
  }

  clearMedia(): void {
    this.selectedMedia = undefined;
    this.documentForm.get('mediaSearch')!.setValue('');
    // Mark form as dirty when image is cleared
    this.documentForm.markAsDirty();
  }

  onHeroMediaSelected(media: Media): void {
    this.selectedHeroMedia = media;
    this.documentForm.get('heroMediaSearch')!.setValue('');
    this.documentForm.markAsDirty();
    this.documentForm.markAsTouched();
  }

  clearHeroMedia(): void {
    this.selectedHeroMedia = undefined;
    this.documentForm.get('heroMediaSearch')!.setValue('');
    this.documentForm.markAsDirty();
  }

  // File upload methods
  dropped(files: NgxFileDropEntry[]): void {
    this.setUploadFileFromDrop(files, false);
  }

  heroDropped(files: NgxFileDropEntry[]): void {
    this.setUploadFileFromDrop(files, true);
  }

  private setUploadFileFromDrop(files: NgxFileDropEntry[], isHero: boolean): void {
    for (const droppedFile of files) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          if (isHero) {
            this.heroUploadFile = file;
            this.updateHeroImageNameValidation();
          } else {
            this.uploadFile = file;
            this.updateImageNameValidation();
          }
          const reader = new FileReader();

          reader.readAsDataURL(file);

          reader.onload = (e) => {
            const image = new Image();
            image.src = e.target!.result as string;

            image.onload = () => {
              if (isHero) {
                this.heroUploadImageWidth = image.width;
                this.heroUploadImageHeight = image.height;
              } else {
                this.uploadImageWidth = image.width;
                this.uploadImageHeight = image.height;
              }
            };
          };
        });
      }
    }
  }

  removeUploadFile(): void {
    this.uploadFile = undefined;
    this.uploadImageWidth = undefined;
    this.uploadImageHeight = undefined;
    this.updateImageNameValidation();
  }

  removeHeroUploadFile(): void {
    this.heroUploadFile = undefined;
    this.heroUploadImageWidth = undefined;
    this.heroUploadImageHeight = undefined;
    this.updateHeroImageNameValidation();
  }

  private updateImageNameValidation(): void {
    const imageNameControl = this.documentForm.get('imageName');
    if (this.uploadFile) {
      // Make imageName required when uploading a file
      imageNameControl?.setValidators([Validators.required]);
    } else {
      // Remove required validator when not uploading
      imageNameControl?.clearValidators();
    }
    imageNameControl?.updateValueAndValidity();
  }

  private updateHeroImageNameValidation(): void {
    const heroImageNameControl = this.documentForm.get('heroImageName');
    if (this.heroUploadFile) {
      heroImageNameControl?.setValidators([Validators.required]);
    } else {
      heroImageNameControl?.clearValidators();
    }
    heroImageNameControl?.updateValueAndValidity();
  }

  uploadImage(): void {
    if (!this.uploadFile) return;

    this.isUploading = true;
    const mediaContext = `${this.featureType}_document_image`;
    const mediaDescription = this.documentForm.get('imageName')?.value || this.uploadFile.name;

    this.uiService
      .waitForEvent(
        this.mediaService.postMedia(
          this.currentAccountID,
          this.uploadFile,
          mediaContext,
          mediaDescription
        )
      )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (response: any) => {
          this.NotificationService.openSuccessSnackBar('Image uploaded successfully');
          // Set the uploaded media as selected
          this.selectedMedia = response;
          // Clear the upload file
          this.removeUploadFile();
          this.isUploading = false;
          // Mark form as dirty when image is uploaded
          this.documentForm.markAsDirty();
        },
        (error) => {
          this.NotificationService.openErrorSnackBar('Error uploading image');
          this.isUploading = false;
        }
      );
  }

  uploadHeroImage(): void {
    if (!this.heroUploadFile) return;

    this.isHeroUploading = true;
    const mediaContext = `${this.featureType}_document_hero_image`;
    const mediaDescription = this.documentForm.get('heroImageName')?.value || this.heroUploadFile.name;

    this.uiService
      .waitForEvent(
        this.mediaService.postMedia(
          this.currentAccountID,
          this.heroUploadFile,
          mediaContext,
          mediaDescription
        )
      )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (response: any) => {
          this.NotificationService.openSuccessSnackBar('Hero image uploaded successfully');
          this.selectedHeroMedia = response;
          this.removeHeroUploadFile();
          this.isHeroUploading = false;
          this.documentForm.markAsDirty();
        },
        () => {
          this.NotificationService.openErrorSnackBar('Error uploading hero image');
          this.isHeroUploading = false;
        }
      );
  }

  get uploadDisabled(): boolean {
    return !this.uploadFile || this.isUploading || !this.documentForm.get('imageName')?.value;
  }

  get heroUploadDisabled(): boolean {
    return !this.heroUploadFile || this.isHeroUploading || !this.documentForm.get('heroImageName')?.value;
  }

  private setupCategorySelect() {
    this.filteredCategories = [...this.allCategories];
    this.categorySelectControl.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntil(this.onDestroy$)
    ).subscribe(val => {
      const filter = typeof val === 'string' ? val.toLowerCase() : '';
      this.updateFilteredCategories(filter);
    });
  }

  private setupTagAutocomplete(categoryId: number) {
    // Initialize filtered tags array for this category
    this.filteredTags[categoryId] = [];
    
    // Set up autocomplete on the tag input for this category
    this.newTagControls[categoryId].valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term: string) => {
        if (!term || term.trim().length < 1) {
          return of([]);
        }
        return this.assististService.getTagsAutocomplete(
          this.currentAccountID,
          this.integrationID,
          categoryId,
          term.trim()
        ).pipe(
          map((resp: any) => resp.records || resp || []),
          catchError(() => of([]))
        );
      }),
      takeUntil(this.onDestroy$)
    ).subscribe(tags => {
      this.filteredTags[categoryId] = tags;
    });
  }

  private updateFilteredCategories(filter: string = '') {
    this.filteredCategories = this.allCategories.filter(cat =>
      !this.categoryKeywords.some(ck => ck.category.id === cat.id) &&
      cat.name.toLowerCase().includes(filter)
    );
  }

  displayCategory(cat?: DocumentCategoryDTO): string {
    return cat ? cat.name : '';
  }

  onCategorySelected(cat: DocumentCategoryDTO) {
    // add a new empty keyword bucket if not already present
    if (!this.categoryKeywords.some(ck => ck.category.id === cat.id)) {
      const newCK: DocumentCategoryKeywordDTO = {
        id: 0,
        document_id: this.document ? this.document.id : 0,
        category_id: cat.id,
        category: cat,
        tags: [],
        is_primary: false
      };
      this.categoryKeywords.push(newCK);
      this.newTagControls[cat.id] = new FormControl('');
      this.setupTagAutocomplete(cat.id);
      this.updateFilteredCategories();
      this.updatePrimaryCategoryOptions();
      // Mark form as dirty when category is added
      this.documentForm.markAsDirty();
      this.categoriesModified = true;
    }
    this.categorySelectControl.setValue('');
  }

  removeTag(ck: DocumentCategoryKeywordDTO, tag: string) {
    ck.tags = ck.tags.filter(t => t.toLowerCase() !== tag.toLowerCase());
    this.updatePrimaryCategoryOptions();
    // Mark form as dirty when tag is removed
    this.documentForm.markAsDirty();
    this.categoriesModified = true;
  }

  addTag(ck: DocumentCategoryKeywordDTO) {
    const ctrl = this.newTagControls[ck.category.id];
    const inputValue = (ctrl.value || '').trim();
    
    if (!inputValue) {
      return;
    }

    // Check if the input contains commas (comma-separated list)
    if (inputValue.includes(',')) {
      // Split by comma and process each tag
      const tags = inputValue.split(',')
        .map((tag: string) => tag.trim().toLowerCase())
        .filter((tag: string) => tag.length > 0) // Remove empty strings
        .filter((tag: string) => !ck.tags.some(existingTag => existingTag.toLowerCase() === tag)); // Remove duplicates

      if (tags.length > 0) {
        ck.tags.push(...tags);
        this.updatePrimaryCategoryOptions();
        // Mark form as dirty when tags are added
        this.documentForm.markAsDirty();
        this.categoriesModified = true;
      }
    } else {
      // Single tag logic (existing functionality)
      const val = inputValue.toLowerCase();
      if (!ck.tags.some(t => t.toLowerCase() === val)) {
        ck.tags.push(val);
        this.updatePrimaryCategoryOptions();
        // Mark form as dirty when tag is added
        this.documentForm.markAsDirty();
        this.categoriesModified = true;
      }
    }
    
    ctrl.setValue('');
  }

  onTagSelected(ck: DocumentCategoryKeywordDTO, selectedTag: string) {
    const trimmedInput = selectedTag.trim();
    
    if (!trimmedInput) {
      return;
    }

    // If the selected tag ends with a colon, put it in the input field for continued typing
    if (trimmedInput.endsWith(':')) {
      this.newTagControls[ck.category.id].setValue(trimmedInput);
      // Clear the filtered tags for this category
      this.filteredTags[ck.category.id] = [];
      return;
    }

    // Check if the selected tag contains commas (comma-separated list)
    if (trimmedInput.includes(',')) {
      // Split by comma and process each tag
      const tags = trimmedInput.split(',')
        .map((tag: string) => tag.trim().toLowerCase())
        .filter((tag: string) => tag.length > 0) // Remove empty strings
        .filter((tag: string) => !ck.tags.some(existingTag => existingTag.toLowerCase() === tag)); // Remove duplicates

      if (tags.length > 0) {
        ck.tags.push(...tags);
        this.updatePrimaryCategoryOptions();
        // Mark form as dirty when tags are added
        this.documentForm.markAsDirty();
        this.categoriesModified = true;
      }
    } else {
      // Single tag logic (existing functionality)
      const tag = trimmedInput.toLowerCase();
      if (!ck.tags.some(t => t.toLowerCase() === tag)) {
        ck.tags.push(tag);
        this.updatePrimaryCategoryOptions();
        // Mark form as dirty when tag is added
        this.documentForm.markAsDirty();
        this.categoriesModified = true;
      }
    }
    
    // Clear the input
    this.newTagControls[ck.category.id].setValue('');
    // Clear the filtered tags for this category
    this.filteredTags[ck.category.id] = [];
  }

  removeCategory(ck: DocumentCategoryKeywordDTO): void {
    // if removing the primary category, clear the primary selection
    if (ck.is_primary) {
      this.primaryCategoryControl.setValue('');
    }
    // drop from the list
    this.categoryKeywords = this.categoryKeywords.filter(x => x.category.id !== ck.category.id);
    // clean up its tag‐input control and filtered tags
    delete this.newTagControls[ck.category.id];
    delete this.filteredTags[ck.category.id];
    // make that category selectable again
    this.updateFilteredCategories();
    this.updatePrimaryCategoryOptions();
    // Mark form as dirty when category is removed
    this.documentForm.markAsDirty();
    this.categoriesModified = true;
  }

  updatePrimaryCategoryOptions(): void {
    // Keep the current primary category only if it still exists.
    const currentPrimary = this.primaryCategoryControl.value;
    if (currentPrimary) {
      const primaryCK = this.categoryKeywords.find(ck => ck.category.id === currentPrimary);
      if (!primaryCK) {
        this.primaryCategoryControl.setValue('');
        this.categoryKeywords.forEach(ck => ck.is_primary = false);
      }
    }
  }

  onPrimaryCategoryChange(categoryId: number): void {
    // Set all categories to non-primary first
    this.categoryKeywords.forEach(ck => ck.is_primary = false);
    
    // Set the selected category as primary if a valid category is selected
    if (categoryId) {
      const selectedCK = this.categoryKeywords.find(ck => ck.category.id === categoryId);
      if (selectedCK) {
        selectedCK.is_primary = true;
      }
    }
    
    // Mark form as dirty when primary category changes to trigger save button
    this.documentForm.markAsDirty();
    this.documentForm.markAsTouched();
    this.categoriesModified = true;
  }

  get primaryCategoryOptions(): DocumentCategoryKeywordDTO[] {
    return this.categoryKeywords;
  }

  get currentPrimaryCategory(): DocumentCategoryKeywordDTO | undefined {
    return this.categoryKeywords.find(ck => ck.is_primary);
  }

  get hasAssignedCategories(): boolean {
    return this.categoryKeywords.length > 0;
  }

  get hasPrimaryCategoryAssigned(): boolean {
    return !!this.currentPrimaryCategory;
  }

  getFilteredTagsForCategory(categoryId: number): string[] {
    return this.filteredTags[categoryId] || [];
  }

  formatEmployeeNames(option: any): string {
    const employees = Array.isArray(option?.employees) ? option.employees : [];
    const names = employees
      .map((employee: any) => typeof employee?.name === 'string' ? employee.name.trim() : '')
      .filter((name: string) => name.length > 0);
    return names.length > 0 ? names.join(', ') : '-';
  }

  formatMealPeriods(option: any): string {
    const periods = Array.isArray(option?.meal_periods) ? option.meal_periods : [];
    const normalized = periods
      .filter((value: any) => typeof value === 'string' && value.trim().length > 0)
      .map((value: string) => value.trim());
    return normalized.length > 0 ? normalized.join(', ') : 'all meals';
  }

  getSortedTagsForCategory(ck: DocumentCategoryKeywordDTO): string[] {
    return [...ck.tags].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }

  get saveDisabled(): boolean {
    // Disable save when form is invalid, or when categories exist but no primary is selected.
    const formInvalid = this.documentForm.invalid;
    const hasChanges = this.hasMeaningfulChanges();
    const missingPrimaryCategory = this.hasAssignedCategories && !this.hasPrimaryCategoryAssigned;
    
    // Allow saving if there are meaningful changes, even if no categories
    if (hasChanges) {
      return formInvalid || missingPrimaryCategory;
    } else {
      return formInvalid || missingPrimaryCategory;
    }
  }

  private hasMeaningfulChanges(): boolean {
    // Check if form is dirty, but exclude changes to mediaSearch field since it's just for searching
    if (this.documentForm.dirty) {
      // If only mediaSearch field is dirty, don't consider it a meaningful change
      const controls = this.documentForm.controls;
      for (const [controlName, control] of Object.entries(controls)) {
        if (controlName !== 'mediaSearch' && controlName !== 'heroMediaSearch' && control.dirty) {
          return true;
        }
      }
    }
    
    // Also check if selectedMedia has changed (this would be a meaningful change)
    if (this.selectedMedia && this.document?.media?.id !== this.selectedMedia.id) {
      return true;
    }
    if (!this.selectedMedia && this.document?.media) {
      return true;
    }
    if (this.selectedHeroMedia && (this.document as any)?.heroMedia?.id !== this.selectedHeroMedia.id) {
      return true;
    }
    if (!this.selectedHeroMedia && (this.document as any)?.heroMedia) {
      return true;
    }
    
    // Check if primary category control is dirty (has been changed)
    if (this.primaryCategoryControl.dirty) {
      return true;
    }
    
    // Check if categories or keywords have been modified
    if (this.categoriesModified) {
      return true;
    }

    // Generated preview changed and should be saved with the document.
    if (this.mockDataModified) {
      return true;
    }
    
    return false;
  }

  private get currentAccountID(): number {
    return this.session.currentAccountValue!.accountID;
  }

  private buildDTO(): any {
    const f = this.documentForm.value;
    const dto: any = {
      name: f.name,
      subtitle: f.subtitle,
      preview: f.preview,
      content: f.content,
      media_id: this.selectedMedia ? this.selectedMedia.id : null,
      hero_media_id: this.selectedHeroMedia ? this.selectedHeroMedia.id : null,
      url: f.url,
      foreign_key: f.foreign_key,
      booking_engine_integration_id: f.booking_engine_integration_id || null,
      mock_data: this.isMockBookingEngineSelected ? (this.generatedMockData || null) : null,
      publish: f.publish,
      publish_start_date: f.publish_start_date || null,
      publish_end_date: f.publish_end_date || null,
      always_include: f.always_include || false,
      feature_start_date: f.feature_start_date || null,
      feature_end_date: f.feature_end_date || null,
      integration_id: this.integrationID,
      documentCategoryKeywords: this.categoryKeywords.map(ck => ({
        id: ck.id || undefined,
        category_id: ck.category.id,
        tags: ck.tags,
        is_primary: ck.is_primary || false
      }))
    };
    if (!this.newDocument) { dto.id = this.document!.id; }
    return dto;
  }

  saveNewDocument() {
    this.uiService.waitForEvent(
      this.assististService.createDocument(this.currentAccountID, this.integrationID, this.buildDTO())
    ).pipe(takeUntil(this.onDestroy$)).subscribe(
      (res: any) => {
        this.NotificationService.openSuccessSnackBar(`${this.itemText} created successfully`);
        // Skip unsaved-changes prompt while routing away after save
        this.ignoreUnsavedChanges = true;
        this.router.navigate(['list'], { relativeTo: this.activatedRoute.parent });
      },
      () => this.NotificationService.openErrorSnackBar(`Error creating ${this.itemText.toLowerCase()}`)
    );
  }

  generateMockBookingData(): void {
    if (this.generateMockDataDisabled) {
      return;
    }

    const formValue = this.documentForm.value;
    this.isGeneratingMockData = true;
    this.uiService.waitForEvent(
      this.assististService.generateDocumentMockBookingData(this.currentAccountID, this.integrationID, {
        document_id: this.document?.id || null,
        name: formValue.name || null,
        subtitle: formValue.subtitle || null,
        preview: formValue.preview || null,
        foreign_key: formValue.foreign_key || null,
        booking_engine_integration_id: formValue.booking_engine_integration_id || null,
        prompt: formValue.mock_data_prompt || '',
        current_mock_data: this.generatedMockData || null,
      })
    ).pipe(takeUntil(this.onDestroy$)).subscribe(
      (response: any) => {
        const generated = this.parseMockData(response?.mock_data);
        if (!generated) {
          this.NotificationService.openErrorSnackBar('Mock booking data generation returned an invalid payload');
          this.isGeneratingMockData = false;
          return;
        }
        this.generatedMockData = generated;
        this.generatedMockDataPromptSnapshot = '';
        this.documentForm.get('mock_data_prompt')?.setValue('', { emitEvent: false });
        this.mockDataModified = true;
        this.documentForm.markAsDirty();
        this.NotificationService.openSuccessSnackBar('Mock booking data generated');
        this.isGeneratingMockData = false;
      },
      () => {
        this.NotificationService.openErrorSnackBar('Error generating mock booking data');
        this.isGeneratingMockData = false;
      }
    );
  }

  saveDocumentUpdates() {
    this.uiService.waitForEvent(
      this.assististService.updateDocument(this.currentAccountID, this.integrationID, this.buildDTO())
    ).pipe(takeUntil(this.onDestroy$)).subscribe(
      () => {
        this.NotificationService.openSuccessSnackBar(`${this.itemText} updated successfully`);
        // Skip unsaved-changes prompt while routing away after save
        this.ignoreUnsavedChanges = true;
        this.router.navigate(['list'], { relativeTo: this.activatedRoute.parent });
      },
      () => this.NotificationService.openErrorSnackBar(`Error updating ${this.itemText.toLowerCase()}`)
    );
  }

  clearMockBookings(): void {
    if (!this.document?.id || !this.canClearMockBookings) {
      return;
    }

    this.dialog.open(AreYouSureDialogComponent, {
      data: {
        areYouSureInfo: {
          description: `Clear all mock booking records for this ${this.itemText.toLowerCase()}?`,
          warning: 'This removes all currently booked mock records for this document.',
        },
      },
    }).afterClosed().pipe(takeUntil(this.onDestroy$)).subscribe((yes) => {
      if (!yes) {
        return;
      }
      this.uiService.waitForEvent(
        this.assististService.clearDocumentMockBookings(this.currentAccountID, this.integrationID, this.document!.id)
      ).pipe(takeUntil(this.onDestroy$)).subscribe(
        () => {
          this.mockBookingsCount = 0;
          this.NotificationService.openSuccessSnackBar('Mock booking records cleared');
        },
        () => this.NotificationService.openErrorSnackBar('Error clearing mock booking records')
      );
    });
  }

  deleteDocument() {
    this.dialog.open(AreYouSureDialogComponent, {
      data: {
        areYouSureInfo: {
          description: `Are you sure you want to delete this ${this.itemText.toLowerCase()}?`,
          warning: 'This is not reversible!',
        },
      },
    }).afterClosed().pipe(takeUntil(this.onDestroy$)).subscribe(yes => {
      if (yes) {
        this.uiService.waitForEvent(
                        this.assististService.deleteDocument(this.currentAccountID, this.integrationID, this.document!.id)
        ).pipe(takeUntil(this.onDestroy$)).subscribe(
          () => {
            this.NotificationService.openSuccessSnackBar(`${this.itemText} deleted successfully`);
            this.router.navigate(['list'], { relativeTo: this.activatedRoute.parent });
          },
          () => this.NotificationService.openErrorSnackBar(`Error deleting ${this.itemText.toLowerCase()}`)
        );
      }
    });
  }

  cancel() {
    this.router.navigate(['list'], { relativeTo: this.activatedRoute.parent });
  }

  onPublishChange(event: any) {
    if (!event.checked) {
      // Clear publish date fields when publish is unchecked
      this.documentForm.patchValue({
        publish_start_date: '',
        publish_end_date: ''
      });
    }
  }

  onFeatureChange(event: any) {
    if (!event.checked) {
      // Clear feature date fields when feature is unchecked
      this.documentForm.patchValue({
        feature_start_date: '',
        feature_end_date: ''
      });
    }
  }

  // CanComponentDeactivate interface implementation
  canDeactivate(): boolean | Observable<boolean> {
    return !this.hasUnsavedChanges();
  }

  hasUnsavedChanges(): boolean {
    // If we explicitly decided to ignore unsaved-changes (e.g., right after
    // saving), bypass the guard.
    if (this.ignoreUnsavedChanges) {
      return false;
    }
    return this.hasMeaningfulChanges();
  }

  // Beforeunload protection
  private addBeforeUnloadListener(): void {
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private removeBeforeUnloadListener(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private beforeUnloadHandler = (event: BeforeUnloadEvent): string | undefined => {
    if (this.hasUnsavedChanges()) {
      const message = 'You have unsaved changes. Are you sure you want to leave?';
      event.preventDefault();
      event.returnValue = message;
      return message;
    }
    return undefined;
  };

  private formatDateForInput(date: Date | string): string {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toISOString().slice(0, 16); // Returns YYYY-MM-DDTHH:mm format for datetime-local
  }

  private featureDateValidator(control: AbstractControl) {
    const formGroup = control as FormGroup;
    const alwaysInclude = formGroup.get('always_include')?.value;
    const featureStartDate = formGroup.get('feature_start_date')?.value;
    const featureEndDate = formGroup.get('feature_end_date')?.value;

    // Check if always_include is required when feature dates are specified
    if ((featureStartDate || featureEndDate) && !alwaysInclude) {
      return { alwaysIncludeRequired: true };
    }

    // Check if start date is before end date
    if (featureStartDate && featureEndDate && new Date(featureStartDate) >= new Date(featureEndDate)) {
      return { featureEndDateBeforeStartDate: true };
    }

    return null;
  }

  private parseMockData(rawMockData: any): any | null {
    if (!rawMockData) {
      return null;
    }
    if (typeof rawMockData === 'string') {
      try {
        return JSON.parse(rawMockData);
      } catch (error) {
        return null;
      }
    }
    if (typeof rawMockData === 'object') {
      return rawMockData;
    }
    return null;
  }

  private getMockToolResponses(): Record<string, any> | null {
    const parsed = this.parseMockData(this.generatedMockData);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    const responses = parsed.tool_responses && typeof parsed.tool_responses === 'object'
      ? parsed.tool_responses
      : parsed;

    if (!responses || typeof responses !== 'object') {
      return null;
    }

    return responses as Record<string, any>;
  }

  private getMockPayloadByAction(actionSuffix: 'get_options' | 'get_availability'): any | null {
    const responses = this.getMockToolResponses();
    if (!responses) {
      return null;
    }

    const providerType = this.selectedBookingEngineProviderType;
    const expectedName = providerType ? this.mockToolNamesByProviderType[providerType]?.[actionSuffix] : null;
    if (expectedName && responses[expectedName]) {
      return responses[expectedName];
    }

    const fallbackKey = Object.keys(responses).find((key) => key.endsWith(`_${actionSuffix}`));
    if (fallbackKey) {
      return responses[fallbackKey];
    }

    return null;
  }
}
